import { useCallback, useEffect, useState } from "react";
import type { NodeDetails, NodeGeometryInfo, SceneNodeInfo, Vec3 } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { Icon } from "../../../kit";
import { CompactCheck, CompactNumber, CompactText, MiniBtn, Row } from "../../inspector/rows";
import { findNodeByGroupId } from "./nodeLookup";
import { useCaps } from "../../CapsProvider";

type Axis = "x" | "y" | "z";
const AXES: Axis[] = ["x", "y", "z"];

/**
 * Resolve the current selection to a scene-tree node id.
 *
 * The selection slice holds a MATERIAL-GROUP id; everything node-addressed
 * needs a node id. `getSceneTree()` is also what re-registers the viewer's id
 * map, so calling it here is load-bearing, not just a lookup: `SceneTree.build`
 * clears `idToObj`, and a node id held across a model reload resolves to
 * nothing until the tree is re-read.
 */
function useSelectedNode(): SceneNodeInfo | null {
  const { client } = useViewer();
  const selection = useDomainState("selection");
  const models = useDomainState("models");
  const [node, setNode] = useState<SceneNodeInfo | null>(null);

  useEffect(() => {
    if (!selection) { setNode(null); return; }
    let alive = true;
    void client.ring
      .getSceneTree()
      .then((tree) => { if (alive) setNode(findNodeByGroupId(tree, selection)); })
      .catch(() => { if (alive) setNode(null); });
    return () => { alive = false; };
  }, [client, selection, models.length]);

  return node;
}

/**
 * The per-mesh section (v58) — the reference's own, minus what we already have.
 *
 * Deliberately does NOT repeat Delete, Fit, Mirror (the floating toolbar) or the
 * pivot buttons (Transform Controls). The one thing that IS repeated is Visible,
 * and it is safe because it reads and writes the same `getNodeDetails` /
 * `setNodeVisible` pair the Hierarchy eye uses — there is no second copy of the
 * value to go stale.
 */
export function NodeInspector() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const node = useSelectedNode();
  const [d, setD] = useState<NodeDetails | null>(null);

  const reload = useCallback(async () => {
    if (!node) { setD(null); return; }
    setD(await r.getNodeDetails(node.id).catch(() => null));
  }, [r, node]);

  useEffect(() => { void reload(); }, [reload]);

  /**
   * ⚠️ Reconcile from the event, do not poll.
   *
   * `transformChanged` fires on gizmo drag-end and carries the whole transform.
   * Without this, dragging the gizmo leaves these numbers showing the pre-drag
   * values — a readout that lies, which is the exact class of defect this
   * section exists to remove.
   */
  useEffect(() => {
    const off = client.on("transformChanged", (e) => {
      if (!node || e.nodeId !== node.id) return;
      setD((cur) =>
        cur ? { ...cur, position: e.position, rotationDeg: e.rotationDeg, scale: e.scale } : cur,
      );
    });
    return off;
  }, [client, node]);

  if (!caps.ringNodeInspectorV58) {
    return (
      <span className="block px-3 py-1.5 text-[11px] leading-snug text-ed-text-3">
        Needs a newer viewer (contract v58). These are five brand-new method
        names, and RPC has no timeout — calling them on an older viewer would
        hang, not fail.
      </span>
    );
  }
  if (!node) return <span className="px-3 py-1.5 text-[11px] text-ed-text-3">Select a part first.</span>;
  if (!d) {
    return (
      <span className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-ed-text-3">
        <Icon name="progress_activity" size={12} className="animate-spin" /> Reading node…
      </span>
    );
  }

  const writeVec = (field: "position" | "rotationDeg" | "scale", axis: Axis, v: number) => {
    setD((cur) => (cur ? { ...cur, [field]: { ...cur[field], [axis]: v } } : cur));
    void r.setNodeTransform(node.id, { [field]: { [axis]: v } } as { position?: Partial<Vec3> });
  };

  const vecRow = (label: string, field: "position" | "rotationDeg" | "scale", step: number) => (
    <Row label={label}>
      {AXES.map((a) => (
        <CompactNumber
          key={a}
          value={Number((d[field][a] ?? 0).toFixed(4))}
          step={step}
          width={58}
          onChange={(v) => writeVec(field, a, v)}
        />
      ))}
    </Row>
  );

  const flag = (label: string, key: "castShadow" | "receiveShadow" | "frustumCulled") => (
    <Row label={label}>
      <CompactCheck
        checked={d[key]}
        onChange={(v) => {
          setD((cur) => (cur ? { ...cur, [key]: v } : cur));
          void r.setNodeFlags(node.id, { [key]: v });
        }}
      />
    </Row>
  );

  return (
    <div className="flex flex-col">
      <Row label="Visible">
        <CompactCheck
          checked={d.visible}
          onChange={(v) => {
            setD((cur) => (cur ? { ...cur, visible: v } : cur));
            void r.setNodeVisible(node.id, v);
          }}
        />
      </Row>
      <Row label="Name">
        {/* CompactText already fires onChange on BLUR only, which is what this
            wants: renameNode triggers onSceneTreeChange and a whole-tree
            re-read, so per-keystroke would be one of those per character. */}
        <CompactText value={d.name} onChange={(v) => { void r.renameNode(node.id, v); void reload(); }} />
      </Row>

      {vecRow("Position", "position", 0.01)}
      {vecRow("Rotation°", "rotationDeg", 1)}
      {vecRow("Scale", "scale", 0.01)}

      <Row label="Render Order">
        <CompactNumber
          value={d.renderOrder}
          step={1}
          width={70}
          onChange={(v) => {
            setD((cur) => (cur ? { ...cur, renderOrder: v } : cur));
            void r.setNodeFlags(node.id, { renderOrder: v });
          }}
        />
      </Row>
      {flag("Casts Shadow", "castShadow")}
      {flag("Receives Shadow", "receiveShadow")}
      {flag("Frustum Culled", "frustumCulled")}

      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        {/* setPivot, NOT autoCenter(): Viewer.autoCenter is literally
            resetView() — a camera reset, not a per-mesh centring. */}
        <MiniBtn icon="filter_center_focus" onClick={() => void r.setPivot(node.id, "center")}>
          Pivot to centre
        </MiniBtn>
        <MiniBtn icon="my_location" onClick={() => void r.focusSelected()}>
          Focus
        </MiniBtn>
      </div>
    </div>
  );
}

/**
 * The Geometry section (v58).
 *
 * Its own RPC rather than a field on `getNodeDetails`, because counting
 * traverses the subtree and this section is collapsed by default — the cost is
 * paid when someone opens it, not on every selection change.
 *
 * Draw calls are absent on purpose: `renderer.info` is per-frame and
 * scene-wide, so there is no honest per-node number to show. (And for the
 * record: I measured that the reference HAS a Geometry section, between the
 * per-mesh one and Physical Material. I never expanded it — these three figures
 * are our choice, not a transcription.)
 */
export function NodeGeometry() {
  const { client } = useViewer();
  const caps = useCaps();
  const node = useSelectedNode();
  const [g, setG] = useState<NodeGeometryInfo | null>(null);

  useEffect(() => {
    if (!node) { setG(null); return; }
    let alive = true;
    void client.ring
      .getNodeGeometryInfo(node.id)
      .then((x) => { if (alive) setG(x); })
      .catch(() => { if (alive) setG(null); });
    return () => { alive = false; };
  }, [client, node]);

  if (!caps.ringNodeInspectorV58) return null;
  if (!node) return <span className="px-3 py-1.5 text-[11px] text-ed-text-3">Select a part first.</span>;
  if (!g) {
    return (
      <span className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-ed-text-3">
        <Icon name="progress_activity" size={12} className="animate-spin" /> Counting…
      </span>
    );
  }

  const n = (v: number) => v.toLocaleString();
  return (
    <div className="flex flex-col">
      <Row label="Vertices"><span className="text-[12px] text-ed-text">{n(g.vertices)}</span></Row>
      <Row label="Triangles"><span className="text-[12px] text-ed-text">{n(g.triangles)}</span></Row>
      <Row label="Meshes"><span className="text-[12px] text-ed-text">{n(g.meshes)}</span></Row>
    </div>
  );
}
